/**
 * Factory helpers to create common calculator patterns.
 * This avoids duplicating calculation logic across hundreds of tools.
 */
import type { ToolConfig, CategorySlug, InputConfig, ResultField, ChartSegment, FAQ } from "./types";

/* ═══════════════════════════════════════════════
   EMI Calculator Factory
   P × r × (1+r)^n / ((1+r)^n − 1)
   ═══════════════════════════════════════════════ */
interface EMIFactoryConfig {
  slug: string;
  category: CategorySlug;
  title: string;
  shortTitle: string;
  description: string;
  keywords: string[];
  defaultAmount?: number;
  maxAmount?: number;
  amountStep?: number;
  amountLabel?: string;
  defaultRate?: number;
  defaultTenure?: number;
  maxTenure?: number;
  whatIs?: string;
  faqs?: FAQ[];
  relatedSlugs?: string[];
  documentType?: string;
  sections?: { title: string; body: string }[];
  /** Override default inputs for custom calculators */
  inputs?: InputConfig[];
  /** Override default calculate for custom calculators */
  calculate?: ToolConfig["calculate"];
  /** Override default result fields */
  resultFields?: ResultField[];
  /** Override default chart segments */
  chartSegments?: ChartSegment[];
  /** Hide chart */
  hideChart?: boolean;
  /** Override content */
  content?: Partial<ToolConfig["content"]>;
}

export function createEMITool(c: EMIFactoryConfig): ToolConfig {
  const defaultInputs: InputConfig[] = [
    { type: "number", key: "amount", label: c.amountLabel ?? "Loan Amount", prefix: "₹", min: 10000, max: c.maxAmount ?? 50000000, step: c.amountStep ?? 100000, default: c.defaultAmount ?? 1000000 },
    { type: "number", key: "rate", label: "Interest Rate", suffix: "% p.a.", min: 1, max: 30, step: 0.1, default: c.defaultRate ?? 8.5 },
    { type: "number", key: "tenure", label: "Loan Tenure", suffix: "years", min: 1, max: c.maxTenure ?? 30, step: 1, default: c.defaultTenure ?? 20 },
  ];

  const defaultCalculate: ToolConfig["calculate"] = (v) => {
    const P = v.amount as number;
    const annual = v.rate as number;
    const years = v.tenure as number;
    const r = annual / 100 / 12;
    const n = years * 12;
    if (P <= 0 || r <= 0 || n <= 0) return { emi: 0, totalInterest: 0, totalPayment: 0 };
    const emi = P * r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1);
    const totalPayment = emi * n;
    return { emi: Math.round(emi), totalInterest: Math.round(totalPayment - P), totalPayment: Math.round(totalPayment) };
  };

  const defaultResultFields: ResultField[] = [
    { key: "emi", label: "Monthly EMI", format: "currency", isPrimary: true },
    { key: "totalInterest", label: "Total Interest", format: "currency" },
    { key: "totalPayment", label: "Total Payment", format: "currency" },
  ];

  const defaultChartSegments: ChartSegment[] = [
    { label: "Principal", valueKey: "amount", color: "#3ecf8e" },
    { label: "Interest", valueKey: "totalInterest", color: "#f97316" },
  ];

  return {
    slug: c.slug,
    category: c.category,
    title: c.title,
    shortTitle: c.shortTitle,
    description: c.description,
    keywords: c.keywords,
    inputs: c.inputs ?? defaultInputs,
    calculate: c.calculate ?? defaultCalculate,
    resultFields: c.resultFields ?? defaultResultFields,
    chartSegments: c.hideChart ? undefined : (c.chartSegments ?? defaultChartSegments),
    hideChart: c.hideChart,
    content: {
      whatIs: c.content?.whatIs ?? c.whatIs,
      formula: c.content?.formula ?? {
        expression: "EMI = P × r × (1+r)^n / ((1+r)^n − 1)",
        variables: [
          { symbol: "P", description: "Principal loan amount" },
          { symbol: "r", description: "Monthly interest rate (annual rate / 12 / 100)" },
          { symbol: "n", description: "Total number of monthly instalments (years × 12)" },
        ],
      },
      faqs: c.content?.faqs ?? c.faqs,
      relatedSlugs: c.content?.relatedSlugs ?? c.relatedSlugs,
      sections: c.content?.sections ?? c.sections,
    },
    documentType: c.documentType,
  };
}

/* ═══════════════════════════════════════════════
   Compound Interest Factory
   A = P × (1 + r/n)^(n×t)
   ═══════════════════════════════════════════════ */
interface CompoundInterestFactoryConfig {
  slug: string;
  category: CategorySlug;
  title: string;
  shortTitle: string;
  description: string;
  keywords: string[];
  defaultAmount?: number;
  maxAmount?: number;
  defaultRate?: number;
  defaultYears?: number;
  maxYears?: number;
  compoundingFrequency?: number; // default 4 (quarterly)
  whatIs?: string;
  faqs?: FAQ[];
  relatedSlugs?: string[];
  sections?: { title: string; body: string }[];
  inputs?: InputConfig[];
  calculate?: ToolConfig["calculate"];
  resultFields?: ResultField[];
  chartSegments?: ChartSegment[];
  hideChart?: boolean;
  content?: Partial<ToolConfig["content"]>;
  documentType?: string;
}

export function createCompoundInterestTool(c: CompoundInterestFactoryConfig): ToolConfig {
  const freq = c.compoundingFrequency ?? 4;
  return {
    slug: c.slug,
    category: c.category,
    title: c.title,
    shortTitle: c.shortTitle,
    description: c.description,
    keywords: c.keywords,
    inputs: c.inputs ?? [
      { type: "number", key: "principal", label: "Investment Amount", prefix: "₹", min: 1000, max: c.maxAmount ?? 10000000, step: 1000, default: c.defaultAmount ?? 100000 },
      { type: "number", key: "rate", label: "Interest Rate", suffix: "% p.a.", min: 1, max: 20, step: 0.1, default: c.defaultRate ?? 7 },
      { type: "number", key: "years", label: "Time Period", suffix: "years", min: 1, max: c.maxYears ?? 30, step: 1, default: c.defaultYears ?? 10 },
    ],
    calculate: c.calculate ?? ((v) => {
      const P = v.principal as number;
      const r = (v.rate as number) / 100;
      const t = v.years as number;
      const A = P * Math.pow(1 + r / freq, freq * t);
      return { maturityAmount: Math.round(A), totalInterest: Math.round(A - P), investedAmount: P };
    }),
    resultFields: c.resultFields ?? [
      { key: "maturityAmount", label: "Maturity Amount", format: "currency", isPrimary: true },
      { key: "totalInterest", label: "Total Interest Earned", format: "currency" },
      { key: "investedAmount", label: "Invested Amount", format: "currency" },
    ],
    chartSegments: c.hideChart ? undefined : (c.chartSegments ?? [
      { label: "Invested", valueKey: "investedAmount", color: "#3ecf8e" },
      { label: "Interest", valueKey: "totalInterest", color: "#8b5cf6" },
    ]),
    hideChart: c.hideChart,
    content: {
      whatIs: c.content?.whatIs ?? c.whatIs,
      formula: c.content?.formula ?? {
        expression: "A = P × (1 + r/n)^(n×t)",
        variables: [
          { symbol: "A", description: "Maturity amount" },
          { symbol: "P", description: "Principal amount invested" },
          { symbol: "r", description: "Annual interest rate (as decimal)" },
          { symbol: "n", description: `Compounding frequency per year (${freq})` },
          { symbol: "t", description: "Time period in years" },
        ],
      },
      faqs: c.content?.faqs ?? c.faqs,
      relatedSlugs: c.content?.relatedSlugs ?? c.relatedSlugs,
      sections: c.content?.sections ?? c.sections,
    },
    documentType: c.documentType,
  };
}

/* ═══════════════════════════════════════════════
   SIP Calculator Factory
   M = P × ((1+r)^n − 1) / r × (1+r)
   ═══════════════════════════════════════════════ */
interface SIPFactoryConfig {
  slug: string;
  category: CategorySlug;
  title: string;
  shortTitle: string;
  description: string;
  keywords: string[];
  defaultMonthly?: number;
  maxMonthly?: number;
  defaultRate?: number;
  defaultYears?: number;
  whatIs?: string;
  faqs?: FAQ[];
  relatedSlugs?: string[];
  sections?: { title: string; body: string }[];
  inputs?: InputConfig[];
  calculate?: ToolConfig["calculate"];
  resultFields?: ResultField[];
  chartSegments?: ChartSegment[];
  hideChart?: boolean;
  content?: Partial<ToolConfig["content"]>;
  documentType?: string;
}

export function createSIPTool(c: SIPFactoryConfig): ToolConfig {
  return {
    slug: c.slug,
    category: c.category,
    title: c.title,
    shortTitle: c.shortTitle,
    description: c.description,
    keywords: c.keywords,
    inputs: c.inputs ?? [
      { type: "number", key: "monthly", label: "Monthly Investment", prefix: "₹", min: 500, max: c.maxMonthly ?? 500000, step: 500, default: c.defaultMonthly ?? 10000 },
      { type: "number", key: "rate", label: "Expected Return Rate", suffix: "% p.a.", min: 1, max: 30, step: 0.5, default: c.defaultRate ?? 12 },
      { type: "number", key: "years", label: "Time Period", suffix: "years", min: 1, max: 40, step: 1, default: c.defaultYears ?? 10 },
    ],
    calculate: c.calculate ?? ((v) => {
      const P = v.monthly as number;
      const annual = v.rate as number;
      const years = v.years as number;
      const r = annual / 100 / 12;
      const n = years * 12;
      if (P <= 0 || r <= 0 || n <= 0) return { totalValue: 0, invested: 0, returns: 0 };
      const FV = P * ((Math.pow(1 + r, n) - 1) / r) * (1 + r);
      const invested = P * n;
      return { totalValue: Math.round(FV), invested, returns: Math.round(FV - invested) };
    }),
    resultFields: c.resultFields ?? [
      { key: "totalValue", label: "Total Value", format: "currency", isPrimary: true },
      { key: "invested", label: "Amount Invested", format: "currency" },
      { key: "returns", label: "Estimated Returns", format: "currency" },
    ],
    chartSegments: c.hideChart ? undefined : (c.chartSegments ?? [
      { label: "Invested", valueKey: "invested", color: "#3ecf8e" },
      { label: "Returns", valueKey: "returns", color: "#8b5cf6" },
    ]),
    hideChart: c.hideChart,
    content: {
      whatIs: c.content?.whatIs ?? c.whatIs,
      formula: c.content?.formula ?? {
        expression: "FV = P × ((1+r)^n − 1) / r × (1+r)",
        variables: [
          { symbol: "FV", description: "Future value of the investment" },
          { symbol: "P", description: "Monthly SIP amount" },
          { symbol: "r", description: "Monthly return rate (annual rate / 12 / 100)" },
          { symbol: "n", description: "Total number of months (years × 12)" },
        ],
      },
      faqs: c.content?.faqs ?? c.faqs,
      relatedSlugs: c.content?.relatedSlugs ?? c.relatedSlugs,
      sections: c.content?.sections ?? c.sections,
    },
    documentType: c.documentType,
  };
}

/* ═══════════════════════════════════════════════
   Simple Percentage Calculator Factory
   result = amount × rate / 100
   ═══════════════════════════════════════════════ */
interface PercentageFactoryConfig {
  slug: string;
  category: CategorySlug;
  title: string;
  shortTitle: string;
  description: string;
  keywords: string[];
  amountLabel?: string;
  rateLabel?: string;
  resultLabel?: string;
  defaultAmount?: number;
  maxAmount?: number;
  defaultRate?: number;
  maxRate?: number;
  whatIs?: string;
  faqs?: FAQ[];
  relatedSlugs?: string[];
  additionalResults?: (v: Record<string, number | string | boolean>) => Record<string, number | string | boolean>;
  additionalResultFields?: ResultField[];
  sections?: { title: string; body: string }[];
  documentType?: string;
  inputs?: InputConfig[];
  calculate?: ToolConfig["calculate"];
  resultFields?: ResultField[];
  chartSegments?: ChartSegment[];
  hideChart?: boolean;
  content?: Partial<ToolConfig["content"]>;
}

export function createPercentageTool(c: PercentageFactoryConfig): ToolConfig {
  const amtLabel = c.amountLabel ?? "Amount";
  const rtLabel = c.rateLabel ?? "Rate";
  const resLabel = c.resultLabel ?? "Result";
  return {
    slug: c.slug,
    category: c.category,
    title: c.title,
    shortTitle: c.shortTitle,
    description: c.description,
    keywords: c.keywords,
    inputs: c.inputs ?? [
      { type: "number", key: "amount", label: amtLabel, prefix: "₹", min: 0, max: c.maxAmount ?? 100000000, step: 10000, default: c.defaultAmount ?? 1000000 },
      { type: "number", key: "rate", label: rtLabel, suffix: "%", min: 0, max: c.maxRate ?? 100, step: 0.1, default: c.defaultRate ?? 10 },
    ],
    calculate: c.calculate ?? ((v) => {
      const amount = v.amount as number;
      const rate = v.rate as number;
      const result = amount * rate / 100;
      const extras = c.additionalResults ? c.additionalResults(v) : {};
      return { result: Math.round(result), amount, ...extras };
    }),
    resultFields: c.resultFields ?? [
      { key: "result", label: resLabel, format: "currency", isPrimary: true },
      ...(c.additionalResultFields ?? []),
    ],
    content: {
      whatIs: c.content?.whatIs ?? c.whatIs,
      formula: c.content?.formula ?? {
        expression: `${resLabel} = ${amtLabel} × ${rtLabel} / 100`,
        variables: [
          { symbol: amtLabel, description: "Base amount" },
          { symbol: rtLabel, description: "Applicable rate in %" },
        ],
      },
      faqs: c.content?.faqs ?? c.faqs,
      relatedSlugs: c.content?.relatedSlugs ?? c.relatedSlugs,
      sections: c.content?.sections ?? c.sections,
    },
    documentType: c.documentType,
    hideChart: c.hideChart ?? true,
    chartSegments: c.chartSegments,
  };
}
